import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";
import { getStoreScheduleSummaryApi } from "../api/scheduleApi";
import { fetchFaceEnrollmentPhotoApi, FlaggedPunch, listFlaggedPunchesApi, reviewPunchApi, ReviewVerdict } from "../api/kioskApi";
import { fetchPunchPhotoApi } from "../api/timekeepingApi";
import { DP_PAGE, DP_TIMESHEET_PAGE, DP_TIMESHEETS_PAGE } from "../routes";
import { formatDateOnly } from "../utils/format";
import "./Dp.css";
import "./DpEco.css";
import "./Timekeeping.css";

const monthBounds = () => {
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
  const [y, m] = today.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${today.slice(0, 7)}-01`, to: `${today.slice(0, 7)}-${String(last).padStart(2, "0")}` };
};

const VERDICT_LABELS: Record<ReviewVerdict, string> = { OK: "Conferida", SUSPEITA: "Suspeita" };

/**
 * Marcações feitas no quiosque pelos 4 dígitos do CPF (ADR 0031): o DP compara a
 * foto da marcação com a do cadastro e marca "Conferida" ou "Suspeita". A
 * suspeita confirmada se desconsidera no espelho, pelo ajuste de sempre.
 */
export default function FlaggedPunchesPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_TIMESHEETS_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const [params, setParams] = useSearchParams();
  const bounds = monthBounds();
  const from = params.get("de") ?? bounds.from;
  const to = params.get("ate") ?? bounds.to;
  const storeId = params.get("loja") ?? "";
  const situation = params.get("situacao") ?? "pendentes";

  const [stores, setStores] = useState<Array<{ storeId: string; storeName: string }>>([]);
  const [items, setItems] = useState<FlaggedPunch[]>([]);
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState<FlaggedPunch | null>(null);

  const setFilter = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };

  useEffect(() => {
    if (!token) return;
    getStoreScheduleSummaryApi(token)
      .then((s) => setStores(s.stores.map(({ storeId: id, storeName }) => ({ storeId: id, storeName }))))
      .catch(() => setStores([]));
  }, [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const reviewed = situation === "pendentes" ? false : situation === "conferidas" ? true : undefined;
      setItems(await listFlaggedPunchesApi(token, { from, to, storeId: storeId || undefined, reviewed }));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar as marcações.");
    } finally {
      setLoading(false);
    }
  }, [token, from, to, storeId, situation]);

  useEffect(() => {
    load();
  }, [load]);

  const openTimesheet = (p: FlaggedPunch) => {
    const [y, m] = p.day.split("-").map(Number);
    navigate(DP_TIMESHEET_PAGE(p.contractId, y, m));
  };

  const situationBadge = (p: FlaggedPunch) =>
    p.review ? (
      <span className={`dpBadge ${p.review.verdict === "OK" ? "dpBadge--green" : "dpBadge--red"}`}>{VERDICT_LABELS[p.review.verdict]}</span>
    ) : (
      <span className="dpBadge dpBadge--orange">Pendente</span>
    );

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[
              { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
              { label: "Ponto", onClick: () => navigate(DP_TIMESHEETS_PAGE()) },
              { label: "Marcações para conferir" },
            ]}
            title="Marcações para conferir"
            subtitle="Feitas no quiosque da loja pelos 4 dígitos do CPF, quando o rosto não foi reconhecido. Compare a foto da marcação com a do cadastro."
            onBack={goBack}
            backLabel="Voltar ao ponto"
          />

          <div className="dpControls" style={{ flexWrap: "wrap" }}>
            <div className="dpFormGroup" style={{ margin: 0 }}>
              <label htmlFor="flFrom">De</label>
              <input id="flFrom" type="date" className="dpInput" value={from} onChange={(e) => setFilter({ de: e.target.value })} />
            </div>
            <div className="dpFormGroup" style={{ margin: 0 }}>
              <label htmlFor="flTo">Até</label>
              <input id="flTo" type="date" className="dpInput" value={to} onChange={(e) => setFilter({ ate: e.target.value })} />
            </div>
            <div className="dpFormGroup" style={{ margin: 0 }}>
              <label htmlFor="flStore">Loja</label>
              <select id="flStore" className="dpSelect" value={storeId} onChange={(e) => setFilter({ loja: e.target.value })}>
                <option value="">Todas</option>
                {stores.map((s) => (
                  <option key={s.storeId} value={s.storeId}>
                    {s.storeName}
                  </option>
                ))}
              </select>
            </div>
            <div className="dpFormGroup" style={{ margin: 0 }}>
              <label htmlFor="flSituation">Situação</label>
              <select id="flSituation" className="dpSelect" value={situation} onChange={(e) => setFilter({ situacao: e.target.value === "pendentes" ? "" : e.target.value })}>
                <option value="pendentes">Pendentes</option>
                <option value="conferidas">Conferidas</option>
                <option value="todas">Todas</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="dpEmpty">Carregando...</div>
          ) : items.length === 0 ? (
            <div className="dpEmpty">Nenhuma marcação {situation === "pendentes" ? "pendente de conferência" : "no período"}.</div>
          ) : (
            <>
              <div className="dpTableWrapper dpTkTableDesktop">
                <table className="dpTable">
                  <thead>
                    <tr>
                      <th>Dia e hora</th>
                      <th>Colaborador</th>
                      <th>Loja</th>
                      <th>Motivo</th>
                      <th>Situação</th>
                      <th style={{ width: 200 }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((p) => (
                      <tr key={p.id}>
                        <td>
                          {formatDateOnly(p.day)} {p.time}
                        </td>
                        <td>
                          {p.employee.name}
                          {!p.employee.hasFaceEnrollment ? <div className="dpMuted">Sem rosto cadastrado</div> : null}
                        </td>
                        <td>
                          {p.store?.name ?? "—"}
                          {p.device ? <div className="dpMuted">{p.device}</div> : null}
                        </td>
                        <td>{p.reason}</td>
                        <td>{situationBadge(p)}</td>
                        <td>
                          <div className="dpTable__actions">
                            <button className="dpBtn dpBtn--primary dpBtn--small" onClick={() => setReviewing(p)}>
                              {p.review ? "Rever" : "Conferir"}
                            </button>
                            <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => openTimesheet(p)}>
                              Espelho
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="dpTkCards">
                {items.map((p) => (
                  <article key={p.id} className="dpTkCard">
                    <div className="dpTkCard__head">
                      <strong>{p.employee.name}</strong>
                      {situationBadge(p)}
                    </div>
                    <p className="dpMuted">
                      {formatDateOnly(p.day)} {p.time} · {p.store?.name ?? "—"} · {p.reason}
                    </p>
                    <div className="dpTkActions">
                      <button className="dpBtn dpBtn--primary dpBtn--small" onClick={() => setReviewing(p)}>
                        {p.review ? "Rever" : "Conferir"}
                      </button>
                      <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => openTimesheet(p)}>
                        Espelho
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}

          {reviewing && token ? (
            <ReviewModal
              token={token}
              punch={reviewing}
              onClose={() => setReviewing(null)}
              onSaved={async (verdict) => {
                setReviewing(null);
                if (verdict === "SUSPEITA") toast.info("Para tirar a marcação do espelho, desconsidere-a no espelho do colaborador.");
                else toast.success("Marcação conferida.");
                await load();
              }}
              onOpenTimesheet={() => openTimesheet(reviewing)}
            />
          ) : null}
        </div>
      </main>
    </div>
  );
}

/** Foto buscada como blob local (as rotas só respondem a ADMIN e DP). */
function BlobPhoto({ load, alt }: { load: () => Promise<Blob>; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    load()
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <div className="dpTkPhoto">{url ? <img src={url} alt={alt} /> : <span className="dpMuted">{failed ? "Sem foto" : "Carregando..."}</span>}</div>;
}

function ReviewModal({
  token,
  punch,
  onClose,
  onSaved,
  onOpenTimesheet,
}: {
  token: string;
  punch: FlaggedPunch;
  onClose: () => void;
  onSaved: (verdict: ReviewVerdict) => Promise<void>;
  onOpenTimesheet: () => void;
}) {
  const [note, setNote] = useState(punch.review?.note ?? "");
  const [saving, setSaving] = useState(false);

  const save = async (verdict: ReviewVerdict) => {
    setSaving(true);
    try {
      await reviewPunchApi(token, punch.id, { verdict, note: note.trim() || undefined });
      await onSaved(verdict);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao registrar a conferência.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dpModalOverlay">
      <div className="dpModal dpFaceModal">
        <header className="dpModal__header">
          <h2 className="dpModal__title">{punch.employee.name}</h2>
          <button type="button" className="dpModal__close" onClick={onClose} disabled={saving}>
            &times;
          </button>
        </header>
        <div className="dpModal__body">
          <p className="dpMuted">
            {formatDateOnly(punch.day)} às {punch.time} · {punch.store?.name ?? "—"} · {punch.reason} · NSR {punch.nsr}
          </p>
          <div className="dpFlagPhotos">
            <figure>
              <BlobPhoto load={() => fetchPunchPhotoApi(token, punch.id)} alt="Foto da marcação" />
              <figcaption>Na marcação</figcaption>
            </figure>
            <figure>
              {punch.employee.hasFaceEnrollment ? (
                <BlobPhoto load={() => fetchFaceEnrollmentPhotoApi(token, punch.employee.id)} alt="Foto do cadastro" />
              ) : (
                <div className="dpTkPhoto">
                  <span className="dpMuted">Sem rosto cadastrado</span>
                </div>
              )}
              <figcaption>No cadastro</figcaption>
            </figure>
          </div>
          <div className="dpFormGroup" style={{ marginTop: 12 }}>
            <label htmlFor="reviewNote">Observação</label>
            <textarea id="reviewNote" className="dpTextarea" maxLength={500} value={note} disabled={saving} onChange={(e) => setNote(e.target.value)} />
          </div>
          {punch.review ? (
            <p className="dpMuted">
              Última conferência: {VERDICT_LABELS[punch.review.verdict]}
              {punch.review.reviewedBy ? ` por ${punch.review.reviewedBy}` : ""}.
            </p>
          ) : null}
        </div>
        <footer className="dpModal__footer">
          <button type="button" className="dpBtn dpBtn--secondary" onClick={onOpenTimesheet} disabled={saving}>
            Abrir espelho
          </button>
          <button type="button" className="dpBtn dpBtn--danger" onClick={() => save("SUSPEITA")} disabled={saving}>
            Suspeita
          </button>
          <button type="button" className="dpBtn dpBtn--primary" onClick={() => save("OK")} disabled={saving}>
            Conferida
          </button>
        </footer>
      </div>
    </div>
  );
}
